import actionDebugger from '@shoflo/studio-redux/middleware/actionDebugger/actionDebugger';
import apiMiddleware from '@shoflo/studio-redux/middleware/api/api';
import attendeeRosterEmitter from '@shoflo/studio-redux/middleware/attendee/attendeeRosterEmitter';
import toggleBackgroundHiddenMiddleware from '@shoflo/studio-redux/middleware/background/toggleBackgroundHiddenMiddleware';
import presentationControlNotifier from '@shoflo/studio-redux/middleware/presentationControlNotifier/presentationControlNotifier';
import updateSourceChangesToProgram from '@shoflo/studio-redux/middleware/program/updateSourceChangesToProgram';
import updateProgramLayers from '@shoflo/studio-redux/middleware/program/updateProgramLayers';
import programEmitter from '@shoflo/studio-redux/middleware/program/programEmitter';
import showAPIError from '@shoflo/studio-redux/middleware/showAPIError/showAPIError';
import sourceUpdateStatusManager from '@shoflo/studio-redux/middleware/sources/sourceUpdateStatusManager/sourceUpdateStatusManager';
import streamRecordStatus from '@shoflo/studio-redux/middleware/streamRecordStatus/streamRecordStatus';
import { createStore, applyMiddleware, compose } from 'redux';
import { routerMiddleware } from 'connected-react-router';
import { withExtraArgument } from 'redux-thunk';
import { createBrowserHistory } from 'history';
import createRootReducer from 'reducers/rootReducer';
import setProgram from 'actions/Broadcast/program/setProgram';
import { getEnvVar } from '@shoflo/polymer/utils/getEnvVar';
import programSceneLayerUpdaterMiddleware from 'middleware/programSceneLayerUpdater';
import updateStaleProgramMiddleware from 'middleware/updateStaleProgram';
import acceptedKnock from 'middleware/acceptedKnock';
import { setAccessTokenFromQueryString } from 'actions/utils';
import SocketDispatcher from './SocketDispatcher';
import rejectedKnockManager from './subscribers/rejectedKnockManager';
import attendeeCameraStatusEmitter from './subscribers/attendeeCameraStatusEmitter';
import { initialState as initialProgramState } from '@shoflo/studio-redux/reducers/program/program';
import programPresenterBannersUpdater from 'middleware/programPresenterBannersUpdater';
import addAudioPresenterLayerToProgramMiddleware from 'middleware/addAudioPresenterLayerToProgram';
import sli from 'middleware/sli';
import notifyDirectors from 'middleware/notifyDirectors';
import {
  BROADCAST_START_RECORDING,
  BROADCAST_START_RECORDINGS,
  BROADCAST_STOP_RECORDING,
  BROADCAST_STOP_RECORDINGS
} from 'actions/Types';

export const defaultInitialState = {
  program: initialProgramState
};

function createConfiguredStore(initialState = defaultInitialState) {
  setAccessTokenFromQueryString(window, document);

  const socketDispatcher = new SocketDispatcher();
  const socket = socketDispatcher.connect();

  const history = createBrowserHistory();

  const enhancers = [];
  const middleware = [
    withExtraArgument(socket),
    routerMiddleware(history),
    apiMiddleware,
    sli([
      `${BROADCAST_START_RECORDING}_SUCCESS`,
      `${BROADCAST_START_RECORDINGS}_SUCCESS`,
      `${BROADCAST_START_RECORDING}_FAILURE`,
      `${BROADCAST_START_RECORDINGS}_FAILURE`,
      `${BROADCAST_STOP_RECORDING}_SUCCESS`,
      `${BROADCAST_STOP_RECORDINGS}_SUCCESS`,
      `${BROADCAST_STOP_RECORDING}_FAILURE`,
      `${BROADCAST_STOP_RECORDINGS}_FAILURE`
    ]),
    programSceneLayerUpdaterMiddleware,
    updateStaleProgramMiddleware,
    programEmitter(socket),
    programPresenterBannersUpdater,
    notifyDirectors(socket),
    acceptedKnock(socket),
    addAudioPresenterLayerToProgramMiddleware,
    streamRecordStatus(socket),
    showAPIError,
    updateSourceChangesToProgram(setProgram),
    updateProgramLayers(),
    presentationControlNotifier(),
    sourceUpdateStatusManager(),
    attendeeRosterEmitter(socket),
    toggleBackgroundHiddenMiddleware(),
    actionDebugger // this should be the last middleware
  ];

  if (getEnvVar('NODE_ENV') === 'development') {
    const devToolsExtension = window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__;

    if (typeof devToolsExtension === 'function') {
      enhancers.push(devToolsExtension());
    }
  }

  const composedEnhancers = compose(
    applyMiddleware(...middleware),
    ...enhancers
  );

  const store = createStore(
    createRootReducer(history),
    initialState,
    composedEnhancers
  );

  socketDispatcher.bindToStore(store);

  store.subscribe(rejectedKnockManager.bind({ store, socket }));
  store.subscribe(attendeeCameraStatusEmitter.bind({ store, socket }));

  return {
    store,
    history
  };
}

export { createConfiguredStore };
